import type { Map as MapLibreMap } from 'maplibre-gl'

import type { Layer, RefreshOutcome } from '../layer'
import { fetchTimes, nearestIndex, pickFrames, type FrameMode } from './timeSource'
import { getMapTemplate, TILE_SIZE, type WmsTarget } from './wms'

/**
 * A WMS layer with a TIME dimension, drawn as one raster source and one raster
 * layer *per frame*, all mounted, with only the current frame at non-zero
 * opacity.
 *
 * Preloading falls out of that structure: MapLibre requests tiles for every
 * layer whose `visibility` is `visible`, and opacity is not visibility. Probed
 * against nowCOAST on 2026-09-16 — six frames at `raster-opacity: 0` produced
 * six tile requests each, so the loop is smooth from the first pass.
 *
 * `raster-fade-duration: 0` is not optional. MapLibre's default 300 ms
 * cross-fade blends two radar frames into a smear, which reads as motion blur
 * over a thunderstorm and is actively misleading.
 *
 * A frame's source is keyed by its *instant*, not its position in the loop.
 * That is what lets `refresh()` slide the loop forward in place: the frames
 * still in it keep their sources and the tiles already fetched for them, and
 * only the newest one is asked of nowCOAST — where remounting would re-render
 * the whole loop at NOAA's end every few minutes for one new picture.
 */

export interface WmsTimeOptions extends WmsTarget {
  id: string
  attribution: string
  opacity: number
  frameCount: number
  frameMode: FrameMode
}

export class WmsTimeLayer implements Layer {
  readonly id: string
  private frames: Date[] = []
  private map: MapLibreMap | null = null
  private current = 0
  private opacity: number
  private visible = true
  private loaded: Promise<void> | null = null
  /** Keys actually on the map, which after a refresh are not `frames`' keys. */
  private mounted: string[] = []

  constructor(private readonly opts: WmsTimeOptions) {
    this.id = opts.id
    this.opacity = opts.opacity
  }

  get times(): readonly Date[] {
    return this.frames
  }

  currentTime(): Date | null {
    const t = this.frames[this.current]
    // new Date(0) is the "no dimension" sentinel from load().
    return t && t.getTime() !== 0 ? t : null
  }

  load(): Promise<void> {
    this.loaded ??= fetchTimes(this.opts.base, this.opts.layerName).then((all) => {
      this.frames = pickFrames(all, this.opts.frameCount, this.opts.frameMode)
      // No dimension, or the fetch failed: draw the server's default frame,
      // which for an observed layer is the latest one.
      if (this.frames.length === 0) this.frames = [new Date(0)]
      this.current = this.opts.frameMode === 'upcoming' ? 0 : this.frames.length - 1
    })
    return this.loaded
  }

  private key(i: number): string {
    return `wx:${this.id}:${this.frames[i].getTime()}`
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    this.mounted = this.frames.map((_, i) => this.key(i))
    this.frames.forEach((t, i) => {
      const key = this.key(i)
      if (map.getLayer(key)) return
      // new Date(0) is the "no dimension" sentinel from load(): omit TIME and
      // let the server pick.
      const time = t.getTime() === 0 ? undefined : t.toISOString()
      map.addSource(key, {
        type: 'raster',
        tiles: [getMapTemplate(this.opts, time)],
        tileSize: TILE_SIZE,
        attribution: this.opts.attribution,
      })
      map.addLayer(
        {
          id: key,
          type: 'raster',
          source: key,
          layout: { visibility: this.visible ? 'visible' : 'none' },
          paint: {
            'raster-opacity': i === this.current ? this.opacity : 0,
            'raster-fade-duration': 0,
          },
        },
        beforeId,
      )
    })
  }

  remove(map: MapLibreMap): void {
    for (const key of this.mounted) unmount(map, key)
    this.mounted = []
    if (this.map === map) this.map = null
  }

  /**
   * Re-read the dimension and slide the loop to its newest frames.
   *
   * Patched in place whenever one frame of the old loop survives, which is
   * every refresh of a page somebody is watching: the new frames go in beside
   * a survivor — the stack position this layer already holds — and the ones
   * that fell off the far end are dropped. A phone back after an hour shares
   * no frame with the loop it left, has no survivor to stand beside, and asks
   * the controller for a remount, which is what toggling the layer did by hand.
   */
  async refresh(canPatch: () => boolean): Promise<RefreshOutcome> {
    // Nothing to refresh before the first load, and nothing to race it with.
    if (!this.loaded) return 'unchanged'
    await this.loaded
    const all = await fetchTimes(this.opts.base, this.opts.layerName)
    // A failed fetch keeps the loop on screen. It was right a moment ago, and
    // swapping it for the server's default frame would hide that anything
    // went wrong.
    if (all.length === 0) return 'unchanged'
    const next = pickFrames(all, this.opts.frameCount, this.opts.frameMode)
    if (sameInstants(next, this.frames)) return 'unchanged'

    const shown = this.frames[this.current]?.getTime()
    this.frames = next
    const kept = next.findIndex((t) => t.getTime() === shown)
    // The frame on screen stays on screen if it is still in the loop, so a
    // user who scrubbed back is not moved. Otherwise the live end, as load().
    this.current =
      kept >= 0 ? kept : this.opts.frameMode === 'upcoming' ? 0 : this.frames.length - 1

    const map = this.map
    if (!map || !canPatch()) return 'remount'
    const wanted = new Set(this.frames.map((_, i) => this.key(i)))
    const anchor = this.mounted.find((key) => wanted.has(key))
    if (!anchor) return 'remount'
    for (const key of this.mounted) if (!wanted.has(key)) unmount(map, key)
    // `add` skips every key already on the map and inserts the rest under the
    // survivor, which is exactly where this overlay sits in the stack.
    this.add(map, anchor)
    // A surviving frame was mounted at opacity 0 unless it was the one showing.
    this.paint(this.current, this.opacity)
    return 'patched'
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    this.paint(this.current, opacity)
  }

  setTime(time: Date): void {
    const next = nearestIndex(this.frames, time)
    if (next < 0 || next === this.current) return
    // Order matters: raise the incoming frame before dropping the outgoing one,
    // or the basemap shows through for a frame.
    this.paint(next, this.opacity)
    this.paint(this.current, 0)
    this.current = next
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    const map = this.map
    if (!map) return
    // All frames, not just the current one: `none` is what stops a hidden
    // layer from continuing to fetch tiles it will never draw.
    this.frames.forEach((_, i) => {
      const key = this.key(i)
      if (map.getLayer(key)) map.setLayoutProperty(key, 'visibility', visible ? 'visible' : 'none')
    })
  }

  ready(): Promise<void> {
    const map = this.map
    if (!map) return Promise.resolve()
    return new Promise((resolve) => map.once('idle', () => resolve()))
  }

  private paint(i: number, value: number): void {
    if (!this.frames[i]) return
    const key = this.key(i)
    if (this.map?.getLayer(key)) this.map.setPaintProperty(key, 'raster-opacity', value)
  }
}

function unmount(map: MapLibreMap, key: string): void {
  if (map.getLayer(key)) map.removeLayer(key)
  if (map.getSource(key)) map.removeSource(key)
}

function sameInstants(a: readonly Date[], b: readonly Date[]): boolean {
  return a.length === b.length && a.every((t, i) => t.getTime() === b[i].getTime())
}
